import { formatNumber } from 'utils/formatNumber';
import useSWR from 'swr';
import { cardsService } from 'views/services/cardsService';
import clsx from 'clsx';
import { User } from 'views/types/typeUser';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import TooltipHorizon from 'components/tooltip';

interface UserCard {
  id: string;
  status: string;
  mask: string;
  card_number: string;
  holder_name: string;
  created_at: string;
  // Cached USD balance from the provider (PayndaPay). Cards are USD-only now.
  balance: number;
  blocked_reason?: string;
  closed_reason?: string;
}

interface IProps {
  user: User;
}

const columnHelper = createColumnHelper<UserCard>();

const columns = [
  columnHelper.accessor('mask', {
    header: 'Card Number',
    cell: info => (
      <span className="font-medium">
        {info.getValue() || info.row.original.card_number || '****'}
      </span>
    ),
  }),
  columnHelper.accessor('status', {
    header: 'Card Status',
    cell: info => {
      const status = info.getValue();
      const isActive = status === 'ACTIVE';

      const badge = (
        <span
          className={clsx(
            'px-2 py-1 rounded-full text-xs font-medium transition-colors duration-200',
            isActive
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-800 cursor-help'
          )}
        >
          {isActive ? 'Active' : 'Inactive'}
        </span>
      );

      return isActive ? (
        badge
      ) : (
        <TooltipHorizon
          trigger={badge}
          content={
            <span className="bg-gray-300 p-2 rounded-md">
              {info.row.original.closed_reason ||
                info.row.original.blocked_reason ||
                'Blocked by user'}
            </span>
          }
          placement="top"
          extra=""
        />
      );
    },
  }),
  columnHelper.accessor('balance', {
    header: 'Card Balance',
    cell: info => {
      const value = Math.max(0, Number(info.getValue() ?? 0));
      return (
        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium dark:bg-blue-900 dark:text-blue-200">
          {formatNumber(value)} USD
        </span>
      );
    },
  }),
];

const UserCardsTab: React.FC<IProps> = ({ user }) => {
  const { data: userCards, isLoading: cardsLoading } = useSWR(
    user?.id ? `/api/cards/user-cards/${user.id}` : null,
    () => cardsService.getUserCards(user.id.toString()),
    { refreshInterval: 10000 }
  );

  const table = useReactTable({
    data: userCards ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalCards = userCards?.length ?? 0;
  const activeCards =
    userCards?.filter(card => card.status === 'ACTIVE')?.length ?? 0;

  return (
    <>
      {cardsLoading ? (
        <span>Loading...</span>
      ) : !userCards || userCards.length === 0 ? (
        <div className="text-center py-8">
          <div className="font-semibold text-gray-500">
            Card Status - Not Issued
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white dark:bg-navy-800 p-4 border border-gray-300 rounded-lg">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Total issued cards
              </h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {totalCards}
              </p>
            </div>
            <div className="bg-white dark:bg-navy-800 p-4 border border-gray-300 rounded-lg">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Active cards
              </h3>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {activeCards}
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-300">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="bg-white pb-2 px-4 pt-4 text-start dark:bg-navy-800"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr
                    key={row.id}
                    className="[&_td]:odd:bg-gray-50 [&_td]:odd:dark:bg-navy-900"
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="p-2 px-4">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

export default UserCardsTab;
